import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Send, Bell, CheckCircle2, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api, { formatError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Invoices() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [sendFor, setSendFor] = useState(null);
  const [sendForm, setSendForm] = useState({ channel: "email", to: "", message: "" });

  const load = async () => setRows((await api.get("/invoices")).data);
  useEffect(() => { load(); }, []);

  const markPaid = async (inv) => {
    await api.patch(`/invoices/${inv.id}/status`, { status: "paid" });
    toast.success(`Invoice ${inv.number} marked paid`);
    load();
  };

  const sendReminder = async (inv) => {
    try {
      await api.post("/reminders/send", { entity_type: "invoice", entity_id: inv.id, channel: "email" });
      toast.success("Reminder sent");
    } catch (e) { toast.error(formatError(e)); }
  };

  const openSend = async (inv) => {
    const c = (await api.get(`/customers/${inv.customer_id}`)).data;
    setSendFor(inv);
    setSendForm({
      channel: "email",
      to: c.email || c.phone || "",
      message: `Hi ${c.name},\n\nAttached is invoice ${inv.number} for ${fmt(inv.total)}. Please remit at your earliest convenience.\n\nThanks!`,
    });
  };

  const doSend = async () => {
    try {
      await api.post("/communications/send", { ...sendForm, subject: `Invoice ${sendFor.number}`, customer_id: sendFor.customer_id, entity_type: "invoice", entity_id: sendFor.id });
      toast.success(`Invoice sent via ${sendForm.channel}`);
      setSendFor(null);
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="invoices-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Invoices</h1>
        <p className="text-sm text-slate-500 mt-1">Track what's paid, what's owed, and what's overdue.</p>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Number</th>
              <th className="text-left px-5 py-3">Customer</th>
              <th className="text-left px-5 py-3">Title</th>
              <th className="text-right px-5 py-3">Total</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Due</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500">No invoices yet. Convert an accepted quote to create one.</td></tr>}
            {rows.map((inv) => (
              <tr key={inv.id} className="odd:bg-slate-50/50 border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono font-semibold text-slate-900"><button onClick={() => nav(`/dashboard/invoices/${inv.id}`)} className="hover:text-blue-600">{inv.number}</button></td>
                <td className="px-5 py-3">{inv.customer_name}</td>
                <td className="px-5 py-3 text-slate-600 max-w-[220px] truncate">{inv.title}</td>
                <td className="px-5 py-3 text-right font-medium">{fmt(inv.total)}</td>
                <td className="px-5 py-3"><span className={`status-pill status-${inv.status}`}>{inv.status}</span></td>
                <td className="px-5 py-3 text-slate-600">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button title="Send" data-testid={`send-inv-${inv.id}`} onClick={() => openSend(inv)} className="p-1.5 hover:bg-slate-100 rounded"><Send className="w-4 h-4 text-blue-600" /></button>
                  <button title="Reminder" data-testid={`remind-inv-${inv.id}`} onClick={() => sendReminder(inv)} className="p-1.5 hover:bg-slate-100 rounded"><Bell className="w-4 h-4 text-amber-600" /></button>
                  <button title="PDF" onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")} className="p-1.5 hover:bg-slate-100 rounded"><Download className="w-4 h-4 text-slate-600" /></button>
                  {inv.status !== "paid" && <button title="Mark paid" data-testid={`paid-inv-${inv.id}`} onClick={() => markPaid(inv)} className="p-1.5 hover:bg-slate-100 rounded"><CheckCircle2 className="w-4 h-4 text-emerald-600" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!sendFor} onOpenChange={(v) => !v && setSendFor(null)}>
        <DialogContent className="max-w-lg" data-testid="send-invoice-dialog">
          <DialogHeader><DialogTitle>Send invoice {sendFor?.number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Channel</Label>
              <Select value={sendForm.channel} onValueChange={(v) => setSendForm({ ...sendForm, channel: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="email"><Mail className="w-3 h-3 inline mr-1" />Email</SelectItem>
                  <SelectItem value="sms"><MessageSquare className="w-3 h-3 inline mr-1" />Text (SMS)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">To</Label>
              <Input value={sendForm.to} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Message</Label>
              <Textarea rows={6} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendFor(null)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={doSend}><Send className="w-4 h-4 mr-1.5" /> Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
